// Ask AI on the Shipping page: "what ships tomorrow?", "what did FedEx take last week?", "lines
// shipped in A2000 but not marked". The page registers itself with the template's chat widget as
// the Worklist does (`axai.ts`); its tools read `/api/routing/ask/shipping` (read only) within the
// page's view — the rows on screen — unless the question asks past it. No $ value here.

import { useEffect, useRef } from "react";
import { APP_PAGES, ROUTING_DEFINITIONS, SHIPPING_GUIDANCE, addDays, weekRange } from "@app/shared";
import { apiPost } from "@axny/platform-ui";
import type { AxaiApp, Tool } from "./axai-prompt";
import type { MyCustomerFilter } from "@app/shared";
import { askPath } from "./axai-prompt";

export interface ShippingAskPage {
  today: string;
  tab: "schedule" | "log";
  /** The shipping rows the schedule shows, and the shipments the Ship log shows, with the filters. */
  scheduleIds: string[];
  shipLogIds: string[];
  /** The filters in words. */
  describe: string;
  /** My customers on: sent with every question, so even one past the view stays within it. */
  mine: MyCustomerFilter | null;
}

const QUERY = {
  from: { type: "string", description: "YYYY-MM-DD: the first pickup date (schedule) or ship date (log)." },
  to: { type: "string", description: "YYYY-MM-DD: the last one." },
  customer: { type: "array", items: { type: "string" }, description: "Customer names or parts of them." },
  carrier: {
    type: "array",
    items: { type: "string" },
    description:
      "Carrier names as asked, or parts of them — never a longer name: 'fedex' finds FedEx, FedEx Freight and " +
      "FedEx Ground.",
  },
  search: { type: "string", description: "A PO, PT / SO #, load # or tracking #." },
  group_by: { type: "string", enum: ["date", "carrier", "customer"] },
  ignore_view: {
    type: "boolean",
    description: "true only when the question asks past the page's filters ('in all warehouses', 'any customer').",
  },
  limit: { type: "integer", description: "Rows to list, 0–25 (default 10). Counts cover every match." },
};

export const SHIPPING_TOOLS: Tool[] = [
  {
    name: "query_shipping_schedule",
    description:
      "Count, group and list the Shipping schedule: lines on scheduled pickups (not shipped yet), with pickup " +
      "date, App Time, carrier, PICK UP #, door, CTNS / PLTS and what to check. Within the page's filters by " +
      "default. 'What ships tomorrow' = from and to tomorrow.",
    input_schema: { type: "object", properties: QUERY },
  },
  {
    name: "query_ship_log",
    description:
      "Count, group and list the Ship log: lines marked shipped in this app, with ship date, carrier, PICK UP # " +
      "/ BOL, tracking and who shipped them. Within the page's filters (ship dates, customer) by default; give " +
      "from / to for other dates and ignore_view: true.",
    input_schema: { type: "object", properties: QUERY },
  },
  {
    name: "shipped_in_a2000_not_marked",
    description:
      "Open Worklist lines whose pick tickets the WMS has SHIPPED or BILLED but nobody marked shipped in the app " +
      "yet, with whether they are on a pickup. A flag: the team marks them shipped.",
    input_schema: { type: "object", properties: { customer: QUERY.customer, limit: QUERY.limit } },
  },
  {
    name: "shipped_in_deposco_not_marked",
    description:
      "Open Deposco lines whose SOs Deposco shows shipped (Ship Complete, or a ship date) but nobody marked " +
      "shipped in the app yet, with whether they are on a pickup. A flag: the team marks them shipped.",
    input_schema: { type: "object", properties: { customer: QUERY.customer, limit: QUERY.limit } },
  },
];

type Input = {
  from?: string;
  to?: string;
  customer?: string[];
  carrier?: string[];
  search?: string;
  group_by?: string;
  ignore_view?: boolean;
  limit?: number;
};

function systemPrompt(p: ShippingAskPage) {
  // "Last week" is the Ship log's own "Last 7 days" (today too); the Monday-to-Sunday week before is asked by name.
  const week = weekRange(p.today, "this");
  const last = { from: addDays(p.today, -6), to: p.today };
  const monday = addDays(week.to, -6);
  const previous = { from: addDays(monday, -7), to: addDays(monday, -1) };
  return [
    {
      type: "text" as const,
      text: [
        "You are Ask AI in Production & Routing, the AXNY warehouse's app. The user is on the Shipping page, " +
          `${p.tab === "schedule" ? "Shipping schedule" : "Ship log"} tab, with ${p.describe || "no filters"}. On ` +
          `screen: ${p.scheduleIds.length} schedule rows, ${p.shipLogIds.length} Ship log rows.`,
        APP_PAGES,
        `Today is ${p.today} (America/New_York); tomorrow is ${addDays(p.today, 1)}; "last week" is the last 7 days, ` +
          `${last.from} to ${last.to} (the Ship log's "Last 7 days"); "the previous calendar week" is ${previous.from} ` +
          `to ${previous.to} (Monday to Sunday); "this week" is ${week.from} to ${week.to}.`,
        "## Definitions",
        `- ${SHIPPING_GUIDANCE}`,
        ...ROUTING_DEFINITIONS.slice(0, 2).map((d) => `- ${d}`),
        "## Rules",
        "- Take every number from a tool answer. A count is within the page's filters unless the question asks " +
          "past them (ignore_view): say the scope in brackets.",
        "- You only read: you cannot schedule, ship or change anything, and must not offer to. The team does it on " +
          "the page (Schedule pickup on the Worklist, Mark shipped on a row).",
        "- Never give an order $ value here.",
        "- When the question names a period ('last week', 'tomorrow'), say its dates in the answer: 'FedEx took 4 " +
          "lines in the last 7 days (10/16–10/22)'.",
        "- Answer briefly, with customers, dates and carriers where it helps.",
      ].join("\n"),
    },
  ];
}

export function useShippingAskAi(page: ShippingAskPage): void {
  const ref = useRef(page);
  ref.current = page;
  useEffect(() => {
    const read = async (body: unknown) => {
      try {
        const mine = ref.current.mine;
        return JSON.stringify(await apiPost(askPath("shipping"), { ...(body as object), ...(mine ? { mine } : {}) }));
      } catch (e) {
        return JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
      }
    };
    const app: AxaiApp = {
      systemPrompt: () => systemPrompt(ref.current),
      handleToolCall: async (name, input, appendStatus) => {
        const p = ref.current;
        const { ignore_view, ...q } = (input ?? {}) as Input;
        switch (name) {
          case "query_shipping_schedule":
            appendStatus("Reading the Shipping schedule…");
            return read({ ...q, kind: "schedule", ...(ignore_view ? {} : { within: p.scheduleIds }) });
          case "query_ship_log":
            appendStatus("Reading the Ship log…");
            return read({ ...q, kind: "log", ...(ignore_view || q.from || q.to ? {} : { within: p.shipLogIds }) });
          case "shipped_in_a2000_not_marked":
            appendStatus("Reading what A2000 shipped…");
            return read({ ...q, kind: "a2000" });
          case "shipped_in_deposco_not_marked":
            appendStatus("Reading what Deposco shipped…");
            return read({ ...q, kind: "deposco" });
          default:
            return JSON.stringify({ error: `Unknown tool ${name}` });
        }
      },
    };
    window.__AXAI_APP__ = app;
    window.AXAI_TOOLS = SHIPPING_TOOLS;
    document.body.classList.add("axai-ready");
    return () => {
      if (window.__AXAI_APP__ === app) delete window.__AXAI_APP__;
      window.AXAI_TOOLS = [];
      document.body.classList.remove("axai-ready");
    };
  }, []);
}
